import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { apiGet } from "@/lib/apiAdmin";
import api from "@/lib/api";
import { HUD } from "./theme";
import { GoogleSection } from "./JarvisGoogle";
import { TeamEditor } from "./JarvisTeam";
import { AISection, BankSection, PaymentsSection, BusinessSection, PosSection, SmsSection, VoiceSection } from "./JarvisSettingsMore";
import { BriefingSection, CalendarsSection, DepositSection, MoneyTextSection } from "./JarvisSettingsExtra";
import { ReportsSection } from "./JarvisReports";
import { ApplePaySection } from "./JarvisApplePay";
import { HeroBackgroundSection } from "./JarvisHeroBackground";
import { LetteringSection } from "./JarvisLettering";
import { ContactTypesSection } from "./JarvisContactTypes";
import { AlertsSection, CardFeeSection, SettingsTabs } from "./JarvisSettingsTabs";
import { ServicesManager } from "@/components/admin/ServicesManager";
import { BuilderManager } from "@/components/admin/BuilderManager";

const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className={HUD.label}>{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-gray-500">{hint}</span>}
  </label>
);

const Text = (props) => <input {...props} className={`w-full h-10 rounded-xl px-3 text-sm border ${HUD.input}`} />;

const Section = ({ title, desc, children }) => (
  <div className={`${HUD.panel} p-5 space-y-4`}>
    <div>
      <h3 className="text-white font-['Outfit'] text-lg">{title}</h3>
      {desc && <p className="text-xs text-gray-400 mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);




export const JarvisSettings = ({ onSaved }) => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({});
  const [keys, setKeys] = useState({ vapi_api_key: "", openphone_api_key: "", retell_api_key: "", google_client_secret: "", plaid_secret: "", nmi_security_key: "" });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("general");

  const hydrate = (d) => {
    setS(d);
    setF({
      assistant_name: d.assistant_name, owner_title: d.owner_title, auto_execute: d.auto_execute,
      speak_replies: d.speak_replies, vapi_voice: d.vapi_voice, team: d.team || [],
      vapi_phone_number_id: d.vapi_phone_number_id, openphone_from_number: d.openphone_from_number,
      ai_model: d.ai_model, personality: d.personality, reply_length: d.reply_length, voice_provider: d.voice_provider, voice_human: d.voice_human,
      retell_from_number: d.retell_from_number, retell_agent_id: d.retell_agent_id, business: d.business, tax_rate: d.tax_rate,
      invoice_terms: d.invoice_terms, invoice_prefix: d.invoice_prefix, pos_provider: d.pos_provider, pos_credentials: {},
      google_review_link: d.google_review_link, customer_sms_auto_reply: d.customer_sms_auto_reply,
      followup_text_customers: d.followup_text_customers, followup_message: d.followup_message,
      review_text_enabled: d.review_text_enabled, review_text_delay_min: d.review_text_delay_min,
      review_quiet_start: d.review_quiet_start, review_quiet_end: d.review_quiet_end, contact_types: d.contact_types, plaid_env: d.plaid_env, plaid_client_id: d.plaid_client_id,
      card_fee_pct: d.card_fee_pct, card_fee_default_on: d.card_fee_default_on, vehicle_conditions: d.vehicle_conditions, service_categories: d.service_categories, alert_kinds: d.alert_kinds || {},
      payment_processor: d.payment_processor, nmi_tokenization_key: d.nmi_tokenization_key,
      google_client_id: d.google_client_id, auto_import_jobs: d.auto_import_jobs, route_start: d.route_start,
      deposit_pct: d.deposit_pct, deposit_hold_hours: d.deposit_hold_hours, calendars: d.calendars || [], briefing: d.briefing,
      goal_strictness: d.goal_strictness, money_text: d.money_text,
    });
    setKeys({ vapi_api_key: "", openphone_api_key: "", retell_api_key: "", google_client_secret: "", plaid_secret: "", nmi_security_key: "" });
  };
  useEffect(() => { apiGet("/admin/jarvis/settings").then(hydrate); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/jarvis/settings", { ...f, ...keys });
      hydrate(data);
      toast.success("JARVIS settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="space-y-4 max-w-3xl" data-testid="jarvis-settings">
      <SettingsTabs tab={tab} setTab={setTab} />
      {tab === "general" && <>
      <Section title="Identity" desc={`Represents ${s.business_info.name} · ${s.business_info.phone} · ${s.business_info.shops}`}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Assistant name"><Text value={f.assistant_name} onChange={set("assistant_name")} data-testid="jarvis-set-name" /></Field>
          <Field label="How it addresses you"><Text value={f.owner_title} onChange={set("owner_title")} data-testid="jarvis-set-title" /></Field>
        </div>
        <Field label="Phone voice (Vapi)" hint="Used on every call JARVIS makes or answers.">
          <div className="flex flex-wrap gap-2">
            {s.voices.map((v) => (
              <button key={v} onClick={() => setF({ ...f, vapi_voice: v })} data-testid={`jarvis-voice-${v}`} className={`h-9 px-4 rounded-full text-sm border transition-colors ${f.vapi_voice === v ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/15 text-gray-400 hover:text-white"}`}>
                {v}
              </button>
            ))}
          </div>
        </Field>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-cyan-400/10 p-3">
          <div>
            <p className="text-sm text-white">Auto-execute actions</p>
            <p className="text-xs text-gray-500">Off = JARVIS always asks you to authorize. Tip: you can also say “just do it” for one-off actions.</p>
          </div>
          <Switch checked={!!f.auto_execute} onCheckedChange={(v) => setF({ ...f, auto_execute: v })} data-testid="jarvis-set-auto" />
        </div>
      </Section>

      <Section title="Team & caller ID" desc="These numbers get the full JARVIS (by name) when they call or text. Every other number is treated as a potential customer — JARVIS takes a message and never shares internals. Priority 1 gets briefing calls.">
        <TeamEditor team={f.team} setTeam={(team) => setF({ ...f, team })} />
      </Section>

      <AlertsSection f={f} setF={setF} />
      <BriefingSection f={f} setF={setF} />
      <MoneyTextSection f={f} setF={setF} />
      <ReportsSection />
      <ContactTypesSection f={f} setF={setF} />
      </>}
      {tab === "payments" && <>
      <PaymentsSection f={f} setF={setF} s={s} keys={keys} setKeys={setKeys} />
      <ApplePaySection />
      <CardFeeSection f={f} setF={setF} />
      <DepositSection f={f} setF={setF} />
      <BankSection f={f} setF={setF} s={s} keys={keys} setKeys={setKeys} />
      <PosSection f={f} setF={setF} s={s} />
      </>}
      {tab === "booking" && <><ServicesManager /><BuilderManager /></>}
      {tab === "homepage" && <><HeroBackgroundSection /><LetteringSection /></>}
      {tab === "calendar" && <>
      <CalendarsSection f={f} setF={setF} s={s} />
      <GoogleSection f={f} setF={setF} s={s} keys={keys} setKeys={setKeys} />
      </>}
      {tab === "phone" && <>
      <AISection f={f} setF={setF} s={s} />
      <VoiceSection f={f} setF={setF} s={s} keys={keys} setKeys={setKeys} />
      <SmsSection f={f} setF={setF} s={s} keys={keys} setKeys={setKeys} />
      </>}
      {tab === "business" && <BusinessSection f={f} setF={setF} />}

      <div className={`sticky bottom-4 flex justify-end ${tab === "homepage" || tab === "booking" ? "hidden" : ""}`}>
        <button onClick={save} disabled={saving} data-testid="jarvis-settings-save" className={`${HUD.btn} h-11 px-6 flex items-center gap-2 shadow-[0_0_30px_rgba(34,229,255,0.35)]`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
      </div>
    </div>
  );
};

export default JarvisSettings;
